import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaCalendarAlt, FaClock, FaUsers, FaArrowRight, FaVideo, FaBroadcastTower,
  FaStar, FaPlay, FaLock, FaCertificate, FaAward, FaQuestionCircle,
  FaChevronDown, FaCrown, FaOm, FaLeaf, FaSpa, FaSun, FaRegClock,
  FaGlobeAsia, FaMedal, FaBell
} from "react-icons/fa";
import {
  FiArrowRight, FiCompass, FiShield, FiClock, FiCalendar, FiUsers,
  FiLock, FiPlay, FiCheckCircle, FiStar, FiAward, FiX, FiCheck,
  FiVideo, FiRadio, FiRefreshCw, FiVolume2, FiVolumeX, FiMaximize2
} from "react-icons/fi";
import { GiLotus, GiMeditation, GiSparkles } from "react-icons/gi";
import { toast } from "react-toastify";
import { liveSessionAPI } from "../services/api";

// ─── AUTH HELPER ─────────────────────────────────────────────────────────────
const getAuthState = () => {
  try {
    const token = localStorage.getItem("divyayoga_token");
    const role = localStorage.getItem("divyayoga_role");
    const user = JSON.parse(localStorage.getItem("divyayoga_user") || "null");
    if (token && user) return { loggedIn: true, role, user };
  } catch {}
  return { loggedIn: false, role: null, user: null };
};

// ─── TIER HIERARCHY & RANKINGS ───────────────────────────────────────────────
const TIERS_CONFIG = {
  Free:     { rank: 0, color: "#888888", bg: "rgba(136,136,136,0.12)", border: "rgba(136,136,136,0.25)", label: "Free Trial", badge: "Free" },
  Basic:    { rank: 1, color: "#6B8A5A", bg: "rgba(107,138,90,0.12)",  border: "rgba(107,138,90,0.25)",   label: "Basic Plan", badge: "Basic" },
  Standard: { rank: 2, color: "#F29A0C", bg: "rgba(242,154,12,0.12)",  border: "rgba(242,154,12,0.25)",   label: "Standard Plan", badge: "Standard" },
  Premium:  { rank: 3, color: "#D97706", bg: "rgba(217,119,6,0.14)",   border: "rgba(217,119,6,0.30)",    label: "Premium Plan", badge: "Premium" },
  Pro:      { rank: 4, color: "#9A3412", bg: "rgba(154,52,18,0.16)",   border: "rgba(154,52,18,0.35)",    label: "Master Teacher", badge: "Pro / YTT" },
};

const normalizeTierName = (t) => {
  if (!t) return "Free";
  const low = String(t).toLowerCase().trim();
  if (low.includes("pro") || low.includes("ytt") || low.includes("vip") || low.includes("diamond")) return "Pro";
  if (low.includes("premium") || low.includes("premiun") || low.includes("gold")) return "Premium";
  if (low.includes("standard") || low.includes("silver")) return "Standard";
  if (low.includes("basic") || low.includes("bronze")) return "Basic";
  return "Free";
};

const getTierRank = (tierName) => {
  const norm = normalizeTierName(tierName);
  return TIERS_CONFIG[norm]?.rank ?? 0;
};

// ─── INDIAN STANDARD TIME (IST) HELPERS ──────────────────────────────────────
const formatToIST = (dateInput) => {
  if (!dateInput) return { time: "06:00 AM IST", date: "Scheduled", day: "IST", rawDate: new Date() };
  let dateStr = String(dateInput);
  if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-")) {
    dateStr += "Z";
  }
  const d = new Date(dateStr);
  const safeDate = isNaN(d.getTime()) ? new Date(dateInput) : d;
  
  const optionsTime = { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' };
  const optionsDate = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' };
  const optionsDay = { weekday: 'short', timeZone: 'Asia/Kolkata' };
  
  const timeFormatted = safeDate.toLocaleTimeString('en-IN', optionsTime);
  const dateFormatted = safeDate.toLocaleDateString('en-IN', optionsDate);
  const dayFormatted = safeDate.toLocaleDateString('en-IN', optionsDay);
  
  return {
    time: `${timeFormatted} IST`,
    date: dateFormatted,
    day: dayFormatted,
    rawDate: safeDate,
  };
};

const getISTRange = (startInput, durationMins = 60) => {
  if (!startInput) return "Time in IST";
  let dateStr = String(startInput);
  if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-")) {
    dateStr += "Z";
  }
  const start = new Date(dateStr);
  const safeStart = isNaN(start.getTime()) ? new Date(startInput) : start;
  const end = new Date(safeStart.getTime() + durationMins * 60000);
  
  const opt = { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' };
  const sStr = safeStart.toLocaleTimeString('en-IN', opt);
  const eStr = end.toLocaleTimeString('en-IN', opt);
  return `${sStr} - ${eStr} IST`;
};

// ─── ICE SERVERS FOR WEBRTC STREAM PREVIEW ───────────────────────────────────
const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:openrelay.metered.ca:80" },
    {
      urls: "turn:openrelay.metered.ca:80",
      username: "openrelayproject",
      credential: "openrelayproject"
    },
    {
      urls: "turn:openrelay.metered.ca:443",
      username: "openrelayproject",
      credential: "openrelayproject"
    }
  ]
};

// ─── 3D LUXURY TILT CARD COMPONENT ───────────────────────────────────────────
function LuxuryTiltCard({ children, className = "", maxTilt = 5, style = {} }) {
  const ref = useRef(null);
  const rotateX = useSpring(useMotionValue(0), { stiffness: 220, damping: 22 });
  const rotateY = useSpring(useMotionValue(0), { stiffness: 220, damping: 22 });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    rotateX.set(py * -maxTilt);
    rotateY.set(px * maxTilt);
  };

  const handleMouseLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        transformPerspective: 1200,
        ...style,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── REAL-TIME ADMIN LIVE VIDEO & WEBRTC RECEIVER COMPONENT ──────────────────
function LiveStreamPlayer({ session, isMuted, toggleMute, userRank, previewSecondsLeft, onPreviewExpired, isLoggedIn }) {
  const videoRef = useRef(null);
  const [streamActive, setStreamActive] = useState(false);
  const [connecting, setConnecting] = useState(true);
  const peerConnRef = useRef(null);

  const isDirectVideo = session?.videoUrl && (
    session.videoUrl.startsWith("http") &&
    (session.videoUrl.endsWith(".mp4") ||
     session.videoUrl.endsWith(".webm") ||
     session.videoUrl.endsWith(".m3u8") ||
     session.videoUrl.includes("r2.dev") ||
     session.videoUrl.includes("blob.core"))
  );

  const isYouTube = session?.videoUrl && (session.videoUrl.includes("youtube.com") || session.videoUrl.includes("youtu.be"));

  // WebRTC Receiver Connection when Admin is broadcasting in LiveRoom (ONLY if Logged In)
  useEffect(() => {
    if (!isLoggedIn || isDirectVideo || isYouTube || !session?.id) {
      setConnecting(false);
      return;
    }

    let isCancelled = false;
    let pollInterval = null;
    const myId = `guest_preview_${Math.random().toString(36).substring(2, 7)}`;

    async function initWebRTCPreview() {
      try {
        setConnecting(true);
        const pc = new RTCPeerConnection(ICE_SERVERS);
        peerConnRef.current = pc;

        pc.ontrack = (event) => {
          if (isCancelled) return;
          if (event.streams && event.streams[0]) {
            if (videoRef.current) {
              videoRef.current.srcObject = event.streams[0];
              videoRef.current.play().catch(() => {});
            }
            setStreamActive(true);
            setConnecting(false);
          }
        };

        pc.onicecandidate = (event) => {
          if (event.candidate && !isCancelled) {
            liveSessionAPI.postSignal(session.id, {
              senderId: myId,
              targetId: "instructor",
              type: "candidate",
              payload: JSON.stringify(event.candidate)
            }).catch(() => {});
          }
        };

        // Create receive-only SDP offer for instructor
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true
        });
        await pc.setLocalDescription(offer);

        await liveSessionAPI.postSignal(session.id, {
          senderId: myId,
          targetId: "instructor",
          type: "offer",
          payload: JSON.stringify(offer)
        });

        // Poll for instructor's answer & ICE candidates
        pollInterval = setInterval(async () => {
          if (isCancelled || !peerConnRef.current) return;
          try {
            const res = await liveSessionAPI.getSignals(session.id, myId);
            if (res?.data?.data && Array.isArray(res.data.data)) {
              for (const sig of res.data.data) {
                if (sig.type === "answer" && peerConnRef.current.signalingState !== "stable") {
                  const desc = new RTCSessionDescription(JSON.parse(sig.payload));
                  await peerConnRef.current.setRemoteDescription(desc);
                } else if (sig.type === "candidate") {
                  const candidate = new RTCIceCandidate(JSON.parse(sig.payload));
                  await peerConnRef.current.addIceCandidate(candidate).catch(() => {});
                }
              }
            }
          } catch (err) {
            // silent catch
          }
        }, 1500);

      } catch (err) {
        if (!isCancelled) {
          console.warn("WebRTC preview connection error:", err);
          setConnecting(false);
        }
      }
    }

    initWebRTCPreview();

    return () => {
      isCancelled = true;
      if (pollInterval) clearInterval(pollInterval);
      if (peerConnRef.current) {
        peerConnRef.current.close();
        peerConnRef.current = null;
      }
    };
  }, [session?.id, isDirectVideo, isYouTube, isLoggedIn]);

  // Handle direct video playback muted sync
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // GUEST STATE: If user is not logged in, show the locked guest authentication card
  if (!isLoggedIn) {
    return (
      <div className="relative aspect-video bg-black flex items-center justify-center overflow-hidden rounded-[26px]">
        {/* Ambient Dark Vedic Background */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35 mix-blend-screen"
          style={{ backgroundImage: "url('/assets/live_yoga_hero_bg.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/80 to-black/90" />

        {/* Video Top Floating Badges */}
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-20 pointer-events-none">
          <span className="px-3 py-1 rounded-full bg-[#EF4444] text-white text-[10px] font-black uppercase tracking-wider shadow-lg flex items-center gap-1.5 backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
            Live Now
          </span>
          <span className="px-3 py-1 rounded-full bg-black/80 border border-[#F29A0C] text-[#FFD166] text-[10px] font-bold flex items-center gap-1 backdrop-blur-md">
            <FaLock className="text-[#F29A0C]" />
            <span>Login Required to Stream</span>
          </span>
        </div>

        {/* Center Guest Lock UI */}
        <div className="relative z-10 text-center px-6 py-4 space-y-3 max-w-sm mx-auto">
          <div className="w-14 h-14 rounded-2xl bg-[#F29A0C]/20 border-2 border-[#F29A0C] text-[#FFD166] flex items-center justify-center text-2xl mx-auto shadow-[0_0_30px_rgba(242,154,12,0.45)] animate-pulse">
            <FaLock />
          </div>

          <div className="space-y-1">
            <h4 className="text-white font-extrabold text-sm sm:text-base tracking-wide" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Sign In to Watch Live Stream
            </h4>
            <p className="text-white/75 text-[11px] sm:text-xs leading-relaxed max-w-xs mx-auto">
              Create a free account or sign in to unlock an 8-minute live preview with live instructor broadcast.
            </p>
          </div>

          <div className="flex items-center justify-center gap-2.5 pt-1.5">
            <Link to="/login">
              <button className="px-5 py-2 rounded-full bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200] text-white text-xs font-black shadow-lg hover:scale-105 transition-transform flex items-center gap-1.5 cursor-pointer">
                <FaPlay className="text-[10px]" />
                <span>Log In</span>
              </button>
            </Link>
            <Link to="/register">
              <button className="px-4 py-2 rounded-full bg-white/10 border border-[#F29A0C]/50 text-[#FFD166] text-xs font-bold hover:bg-white/20 transition-all cursor-pointer">
                Create Free Account
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative aspect-video bg-black flex items-center justify-center overflow-hidden rounded-[26px]">
      {/* 1. Direct Video File */}
      {isDirectVideo ? (
        <video
          ref={videoRef}
          src={session.videoUrl}
          autoPlay
          playsInline
          loop
          muted={isMuted}
          className="w-full h-full object-cover"
        />
      ) : isYouTube ? (
        <iframe
          src={`https://www.youtube.com/embed/${session.videoUrl.split("v=")[1]?.split("&")[0] || session.videoUrl.split("/").pop()}?autoplay=1&mute=${isMuted ? 1 : 0}`}
          title="Live Yoga Broadcast"
          className="w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        /* 2. WebRTC Live Video Stream from Admin or Studio Standby Feed */
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={isMuted}
            className={`w-full h-full object-cover ${streamActive ? "block" : "hidden"}`}
          />

          {/* Standby / Active Studio Feed when WebRTC video is buffering or connecting */}
          {!streamActive && (
            <div className="relative w-full h-full flex flex-col items-center justify-center text-center p-6 bg-gradient-to-b from-[#180A03] via-[#2A1206] to-[#120702]">
              <div
                className="absolute inset-0 bg-cover bg-center opacity-40 mix-blend-screen"
                style={{ backgroundImage: "url('/assets/live_yoga_hero_bg.jpg')" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/80" />

              <div className="relative z-10 space-y-3">
                <div className="w-16 h-16 rounded-full bg-[#F29A0C]/20 border-2 border-[#F29A0C] text-[#FFD166] flex items-center justify-center text-2xl mx-auto shadow-[0_0_30px_rgba(242,154,12,0.5)] animate-pulse">
                  <FaBroadcastTower />
                </div>

                <div className="space-y-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EF4444]/20 border border-[#EF4444]/40 text-[#FFA41B] text-[11px] font-black uppercase tracking-wider">
                    <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-ping" />
                    <span>Admin Studio Live Broadcasting</span>
                  </div>
                  <h4 className="text-white font-black text-sm sm:text-base tracking-wide">
                    {session?.instructorName || "Master Faculty"} is Live in Studio
                  </h4>
                  <p className="text-white/70 text-xs max-w-xs mx-auto">
                    Bidirectional live camera stream active. Click below to enter full interactive studio.
                  </p>
                </div>

                <Link to={`/live-room/${session.id}`} className="inline-block pt-1">
                  <button className="px-5 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200] text-white text-xs font-black shadow-lg hover:scale-105 transition-transform flex items-center gap-2 cursor-pointer">
                    <FaVideo className="text-xs" />
                    <span>Enter Live Room</span>
                  </button>
                </Link>
              </div>
            </div>
          )}
        </>
      )}

      {/* Video Top Floating Badges */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-20 pointer-events-none">
        <span className="px-3 py-1 rounded-full bg-[#EF4444] text-white text-[10px] font-black uppercase tracking-wider shadow-lg flex items-center gap-1.5 backdrop-blur-md">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
          Live Studio
        </span>

        {/* Guest / Free User Preview Timer Badge */}
        {userRank === 0 ? (
          <span className="px-3 py-1 rounded-full bg-black/80 border border-[#F29A0C] text-[#FFD166] text-[11px] font-black flex items-center gap-1.5 shadow-lg backdrop-blur-md">
            <FaRegClock className="text-[#F29A0C]" />
            <span>Preview: {Math.floor(previewSecondsLeft / 60)}:{String(previewSecondsLeft % 60).padStart(2, '0')}</span>
          </span>
        ) : (
          <span className="px-3 py-1 rounded-full bg-black/80 border border-[#22C55E]/60 text-[#86EFAC] text-[10px] font-bold flex items-center gap-1.5 shadow-lg backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
            <span>Member Stream</span>
          </span>
        )}
      </div>

      {/* Video Bottom Controls Overlay */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between z-20">
        <button
          onClick={toggleMute}
          className="w-9 h-9 rounded-full bg-black/75 border border-white/30 text-white flex items-center justify-center text-xs hover:bg-[#F29A0C] hover:border-[#F29A0C] transition-all cursor-pointer backdrop-blur-md shadow-md"
          aria-label="Toggle mute"
        >
          {isMuted ? <FiVolumeX size={15} /> : <FiVolume2 size={15} />}
        </button>

        <div className="flex items-center gap-2">
          <div className="text-[11px] font-bold text-white/95 bg-black/75 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/15 shadow-md flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#22C55E]" />
            <span>{session.enrolledCount || 1} Connected</span>
          </div>

          <Link to={`/live-room/${session.id}`}>
            <button
              className="w-9 h-9 rounded-full bg-black/75 border border-white/30 text-white flex items-center justify-center text-xs hover:bg-[#F29A0C] transition-all cursor-pointer backdrop-blur-md shadow-md"
              title="Full Studio"
            >
              <FiMaximize2 size={14} />
            </button>
          </Link>
        </div>
      </div>

      {/* Locked Overlay if Guest Preview Expired */}
      {userRank === 0 && previewSecondsLeft <= 0 && (
        <div className="absolute inset-0 bg-black/90 backdrop-blur-lg flex flex-col items-center justify-center text-center p-6 space-y-3 z-30">
          <div className="w-14 h-14 rounded-2xl bg-[#F29A0C]/20 border border-[#F29A0C] text-[#FFD166] flex items-center justify-center text-2xl animate-bounce">
            <FaLock />
          </div>
          <h4 className="text-white font-extrabold text-base">Free Live Preview Completed</h4>
          <p className="text-white/80 text-xs max-w-xs">
            Upgrade your membership to unlock full interactive 2-way live video classes with posture alignment.
          </p>
          <button
            onClick={onPreviewExpired}
            className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-xs font-black shadow-lg hover:scale-105 transition-transform cursor-pointer"
          >
            Upgrade Membership Plan
          </button>
        </div>
      )}
    </div>
  );
}

const CATEGORIES = [
  { id: "all", label: "All Disciplines" },
  { id: "Asana & Flow", label: "Asana & Flow" },
  { id: "Pranayama", label: "Pranayama" },
  { id: "Therapy", label: "Therapeutic & Spine" },
  { id: "Meditation", label: "Meditation & Nidra" },
  { id: "Teacher Training", label: "Teacher Training" },
];

const TIER_FILTERS = [
  { id: "all", label: "All Memberships" },
  { id: "Free", label: "Free Trial" },
  { id: "Basic", label: "Basic Plan" },
  { id: "Standard", label: "Standard Plan" },
  { id: "Premium", label: "Premium Plan" },
  { id: "Pro", label: "Pro / YTT" },
];

const FAQS = [
  {
    q: "How do two-way live interactive camera corrections work?",
    a: "All Divyyog Live sessions utilize bidirectional high-definition video. Master trainers observe your camera in real time on our instructor console, offering personalized verbal alignment cues for joint safety, spinal protection, and maximum energetic benefit."
  },
  {
    q: "Are all session timings shown in Indian Standard Time (IST)?",
    a: "Yes! Every single scheduled live batch is calculated and displayed in Indian Standard Time (IST / UTC+5:30). Whether you are practicing from India, North America, Europe, or the Middle East, the schedule is precisely converted so you never miss a class."
  },
  {
    q: "Can I switch between morning and evening live batches if my schedule changes?",
    a: "Yes. As a subscribed member, you have full flexibility to attend morning, afternoon, or evening batches according to your daily availability. Simply open the session list and enter the live room."
  },
  {
    q: "What happens if I miss a scheduled live class?",
    a: "Every live masterclass is recorded in high definition and automatically uploaded to your Member Training Library within 2 hours of completion. You can practice with the full recording 24/7."
  },
  {
    q: "How does the Free Guest Preview work for ongoing live sessions?",
    a: "Visitors who haven't subscribed yet can watch 5 to 10 minutes of select ongoing live classes as a free trial preview to experience the instructor's teaching style and video quality before choosing a membership plan."
  }
];

export default function LiveClasses() {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(getAuthState);
  const [rawSessions, setRawSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTier, setSelectedTier] = useState("all");
  const [openFaq, setOpenFaq] = useState(null);

  // ─── LIVE PREVIEW TIMER STATE (FOR GUESTS / FREE TIER) ───
  const [previewSecondsLeft, setPreviewSecondsLeft] = useState(480); // 8 minutes (480s)
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isPlayerMuted, setIsPlayerMuted] = useState(true);

  useEffect(() => {
    setAuth(getAuthState());
  }, []);

  // Fetch real database sessions purely from API (with Auto-Polling every 12s)
  const fetchSessions = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const res = await liveSessionAPI.getSessions();
      if (res?.data?.data && Array.isArray(res.data.data)) {
        setRawSessions(res.data.data);
      } else if (Array.isArray(res?.data)) {
        setRawSessions(res.data);
      } else {
        setRawSessions([]);
      }
    } catch (err) {
      console.error("Error fetching live sessions from API:", err);
      setRawSessions([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions(true);
    const interval = setInterval(() => {
      fetchSessions(false);
    }, 12000); // 12-second live pulse
    return () => clearInterval(interval);
  }, [fetchSessions]);

  // User Tier Calculation
  const userRank = useMemo(() => {
    if (!auth.loggedIn) return 0;
    if (auth.role === "Admin" || auth.role === "SuperAdmin") return 4;
    const tierName = auth.user?.subscriptionTier || auth.user?.tier || "Basic";
    return getTierRank(tierName);
  }, [auth]);

  // Normalize session properties to ensure consistent casing & time calculations
  const normalizedSessions = useMemo(() => {
    const nowTime = Date.now();
    return rawSessions.map((s) => {
      const id = s.id || s.Id || "";
      const title = s.title || s.Title || "Yoga Session";
      const description = s.description || s.Description || "";
      const instructorName = s.instructorName || s.InstructorName || "Master Instructor";
      const scheduledAt = s.scheduledAt || s.ScheduledAt || new Date().toISOString();
      const durationInMinutes = s.durationInMinutes || s.DurationInMinutes || 60;
      const category = s.category || s.Category || "Asana";
      const tier = s.tier || s.Tier || "Free";
      const maxCapacity = s.maxCapacity || s.MaxCapacity || 100;
      const enrolledCount = s.enrolledCount || s.EnrolledCount || 0;
      const videoUrl = s.videoUrl || s.VideoUrl || `/live-room/${id}`;

      let dateStr = String(scheduledAt);
      if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-")) {
        dateStr += "Z";
      }
      const parsedDate = new Date(dateStr);
      const schedTime = isNaN(parsedDate.getTime()) ? new Date(scheduledAt).getTime() : parsedDate.getTime();
      const endTime = schedTime + durationInMinutes * 60000;

      const isLive = s.isLive ?? s.IsLive ?? (nowTime >= schedTime && nowTime <= endTime);
      const isUpcoming = s.isUpcoming ?? s.IsUpcoming ?? (nowTime < schedTime);
      const isEnded = s.isEnded ?? s.IsEnded ?? (nowTime > endTime);

      return {
        id,
        title,
        description,
        instructorName,
        scheduledAt,
        durationInMinutes,
        category,
        tier,
        maxCapacity,
        enrolledCount,
        videoUrl,
        isLive: Boolean(isLive),
        isUpcoming: Boolean(isUpcoming),
        isEnded: Boolean(isEnded),
        schedTime,
        endTime
      };
    });
  }, [rawSessions]);

  // Determine currently active live session (ONLY IF ACTUALLY LIVE)
  const activeLiveSession = useMemo(() => {
    return normalizedSessions.find((s) => s.isLive === true);
  }, [normalizedSessions]);

  // Filter Upcoming & Active Scheduled Sessions from API Data
  const upcomingSessions = useMemo(() => {
    const nowMs = Date.now();
    return normalizedSessions.filter((s) => {
      // Must be upcoming or active live or within recent grace period
      const isEligibleTime = s.isUpcoming || s.isLive || (!s.isEnded && s.schedTime >= nowMs - 15 * 60000);
      if (!isEligibleTime) return false;

      const matchCat = selectedCategory === "all" || s.category.toLowerCase().includes(selectedCategory.toLowerCase()) || selectedCategory.toLowerCase().includes(s.category.toLowerCase());
      const normTier = normalizeTierName(s.tier);
      const matchTier = selectedTier === "all" || normTier === selectedTier;
      return matchCat && matchTier;
    });
  }, [normalizedSessions, selectedCategory, selectedTier]);

  // Guest Live Preview Timer effect
  useEffect(() => {
    if (!activeLiveSession) return;
    if (userRank > 0) return; // Subscribed members have unlimited access
    if (previewSecondsLeft <= 0) {
      setIsPreviewModalOpen(true);
      return;
    }

    const timer = setInterval(() => {
      setPreviewSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsPreviewModalOpen(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [previewSecondsLeft, userRank, activeLiveSession]);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="relative overflow-hidden bg-[#FFF9F2] min-h-screen text-[#111111] pb-24">
      
      {/* ══════════════════════════════════════════════════════════════════════════
          1. ROYAL HERO BANNER (USING BESPOKE GENERATED LIVE YOGA SANCTUARY IMAGE)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[92vh] lg:min-h-screen flex items-center justify-center overflow-hidden">
        
        {/* Generated Live Sanctuary Photographic Artwork */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-[1.02] transition-transform duration-1000"
          style={{
            backgroundImage: "url('/assets/live_yoga_hero_bg.jpg')",
          }}
        />

        {/* Balanced Subtle Vignette: Preserves Rich Architectural & Lighting Details */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(180deg, rgba(14, 7, 2, 0.48) 0%, rgba(14, 7, 2, 0.28) 40%, rgba(16, 8, 3, 0.58) 100%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFF9F2]/20 via-transparent to-black/25 pointer-events-none" />

        {/* Ambient Glowing Aura Orbs */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-[480px] h-[480px] rounded-full bg-[#7D9D64]/25 blur-[120px] pointer-events-none"
        />
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.12, 0.22, 0.12] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
          className="absolute bottom-1/3 right-1/4 w-[480px] h-[480px] rounded-full bg-[#F29A0C]/25 blur-[120px] pointer-events-none"
        />

        {/* Floating Sacred Aura Stardust Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(24)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -40, 0],
                x: [0, ((i * 7) % 30) - 15, 0],
                opacity: [0.25, 0.85, 0.25],
                scale: [1, 1.35, 1],
              }}
              transition={{
                duration: 5 + (i % 5),
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.2,
              }}
              className="absolute rounded-full"
              style={{
                width: `${3 + (i % 4) * 2}px`,
                height: `${3 + (i % 4) * 2}px`,
                backgroundColor: i % 3 === 0 ? "#9DC08B" : i % 3 === 1 ? "#FFD166" : "#FFA41B",
                boxShadow: "0 0 12px rgba(242,154,12,0.8)",
                top: `${10 + (i * 7.5) % 80}%`,
                left: `${5 + (i * 8.2) % 90}%`,
              }}
            />
          ))}
        </div>

        {/* Hero Central Header */}
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center pt-28 pb-20">
          
          {/* Top Royal Live Beacon Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-3 px-6 py-2.5 rounded-full bg-black/65 border border-[#F29A0C]/60 text-[#FFD166] text-xs font-bold uppercase tracking-[0.25em] shadow-[0_10px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl mb-8"
          >
            <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444] animate-ping" />
            <FaBroadcastTower className="text-[#F29A0C]" />
            <span>Live Virtual Studio · 100% Two-Way Interactive</span>
          </motion.div>

          {/* Main Royal Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.15 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[1.2] sm:leading-[1.12] tracking-[0.02em] drop-shadow-[0_6px_25px_rgba(0,0,0,0.9)] max-w-5xl mx-auto"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Practice in Real Time with <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-[#FFD166] via-[#FFA41B] to-[#F29A0C] bg-clip-text text-transparent drop-shadow-[0_4px_30px_rgba(242,154,12,0.7)]">
              Living Yoga Masters
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.3 }}
            className="text-white/95 text-base sm:text-xl font-medium leading-[1.7] tracking-[0.015em] mt-7 max-w-3xl mx-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]"
          >
            All batches scheduled in Indian Standard Time (IST). Experience direct posture corrections, spine alignment geometry, and live energy synchronization from home.
          </motion.p>

          {/* Quick Pillars Strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.45 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto"
          >
            {[
              { label: "Timings", val: "All Batches in IST", icon: FaClock },
              { label: "Instruction", val: "Direct Camera Feedback", icon: FaVideo },
              { label: "Lineage", val: "Yoga Alliance USA", icon: FaCertificate },
              { label: "Community", val: "15+ Countries Live", icon: FaGlobeAsia },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div
                  key={i}
                  className="p-4 rounded-2xl bg-black/60 border border-[#F4D9B5]/30 backdrop-blur-xl text-center space-y-1 shadow-lg"
                >
                  <Icon className="mx-auto text-lg text-[#FFD166]" />
                  <div className="text-white font-bold text-sm tracking-wide">{stat.val}</div>
                  <div className="text-white/70 text-[11px] font-medium">{stat.label}</div>
                </div>
              );
            })}
          </motion.div>

        </div>

      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          2. FEATURED ACTIVE LIVE STUDIO BROADCAST (ONLY SHOWN IF ACTUALLY LIVE NOW)
      ══════════════════════════════════════════════════════════════════════════ */}
      {activeLiveSession && (
        <section className="relative z-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 -mt-12 mb-16">
          <div className="relative">
            {/* Glowing Orange Aura Behind the Live Card */}
            <div className="absolute -inset-2.5 bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E97818] rounded-[48px] blur-2xl opacity-60 pointer-events-none animate-pulse" />

            <div
              className="relative overflow-hidden rounded-[38px] sm:rounded-[44px] border-2 border-[#F29A0C] text-white p-6 sm:p-10 lg:p-12 shadow-[0_0_50px_rgba(242,154,12,0.45),0_30px_90px_rgba(0,0,0,0.6)] backdrop-blur-3xl"
              style={{
                background: "linear-gradient(135deg, rgba(26, 12, 5, 0.96) 0%, rgba(44, 20, 8, 0.93) 50%, rgba(18, 8, 3, 0.98) 100%)",
              }}
            >
              {/* Background Sacred Mandala Art Blend */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-60 mix-blend-screen scale-[1.02] pointer-events-none"
                style={{ backgroundImage: "url('/assets/trainers_bottom_bg.jpg')" }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1A0C05]/95 via-[#2C1408]/75 to-[#1A0C05]/90 pointer-events-none" />

              {/* Top Golden Shimmer Line */}
              <div
                className="absolute top-0 left-0 right-0 h-[4px]"
                style={{
                  background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #FF8400, #F29A0C, transparent)",
                }}
              />

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
                
                {/* Left Col: Live Session Details & Actions (7 Cols) */}
                <div className="lg:col-span-7 space-y-5 text-center lg:text-left">
                  
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                    <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EF4444] text-white text-xs font-black uppercase tracking-wider animate-pulse shadow-md">
                      <span className="w-2 h-2 rounded-full bg-white animate-ping" />
                      Live Stream Active Now
                    </span>
                    <span className="px-3.5 py-1.5 rounded-full bg-black/60 border border-[#F29A0C]/50 text-[#FFD166] text-xs font-bold">
                      {TIERS_CONFIG[normalizeTierName(activeLiveSession.tier)]?.label || "Standard Plan"} Required
                    </span>
                    <span className="px-3 py-1.5 rounded-full bg-white/10 text-white/80 text-xs font-semibold">
                      {activeLiveSession.category || "Asana & Flow"}
                    </span>
                  </div>

                  <h2 className="text-2xl sm:text-4xl font-extrabold text-white leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {activeLiveSession.title}
                  </h2>

                  <p className="text-xs sm:text-sm text-white/90 leading-relaxed max-w-xl mx-auto lg:mx-0 font-normal">
                    {activeLiveSession.description || "Join master yoga instructor live for personalized posture corrections and structured Vedic energy synchronization."}
                  </p>

                  {/* Instructor & Indian Standard Time Info */}
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-5 pt-1 text-xs sm:text-sm text-white/90">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-2xl bg-[#F29A0C]/20 border border-[#F29A0C] text-[#FFD166] flex items-center justify-center text-lg shadow-sm">
                        <GiLotus />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white">{activeLiveSession.instructorName || "Faculty Yoga Master"}</div>
                        <div className="text-[11px] text-[#FFD166] font-semibold">Live Class Instructor</div>
                      </div>
                    </div>

                    <div className="h-8 w-[1px] bg-white/20 hidden sm:block" />

                    <div className="text-left">
                      <div className="flex items-center gap-1.5 text-[#FFD166] font-bold">
                        <FaClock />
                        <span>{getISTRange(activeLiveSession.scheduledAt, activeLiveSession.durationInMinutes)}</span>
                      </div>
                      <div className="text-[11px] text-white/70">
                        {formatToIST(activeLiveSession.scheduledAt).date} (IST)
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons depending on membership & authentication */}
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-3">
                    {!auth.loggedIn ? (
                      <>
                        <Link to="/login">
                          <motion.button
                            whileHover={{ scale: 1.04, y: -2 }}
                            whileTap={{ scale: 0.96 }}
                            className="rounded-full bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200] text-white px-8 py-3.5 text-xs sm:text-sm font-black shadow-[0_15px_40px_rgba(242,154,12,0.65)] hover:shadow-[0_20px_50px_rgba(242,154,12,0.85)] transition-all cursor-pointer flex items-center gap-2 group"
                          >
                            <FaPlay className="text-xs" />
                            <span>Log In to Watch Live</span>
                            <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                          </motion.button>
                        </Link>

                        <Link to="/register">
                          <motion.button
                            whileHover={{ scale: 1.03, y: -2 }}
                            whileTap={{ scale: 0.96 }}
                            className="rounded-full border-2 border-[#F29A0C]/70 bg-black/50 backdrop-blur-xl text-white px-7 py-3.5 text-xs sm:text-sm font-extrabold hover:border-[#FFD166] hover:bg-[#F29A0C]/20 transition-all cursor-pointer flex items-center gap-2 shadow-lg"
                          >
                            <FaCrown className="text-[#FFD166] text-sm" />
                            <span>Create Free Account</span>
                          </motion.button>
                        </Link>
                      </>
                    ) : userRank >= getTierRank(activeLiveSession.tier) ? (
                      <Link to={`/live-room/${activeLiveSession.id}`}>
                        <motion.button
                          whileHover={{ scale: 1.04, y: -2 }}
                          whileTap={{ scale: 0.96 }}
                          className="rounded-full bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200] text-white px-9 py-4 text-xs sm:text-sm font-black shadow-[0_15px_40px_rgba(242,154,12,0.65)] hover:shadow-[0_20px_50px_rgba(242,154,12,0.85)] transition-all cursor-pointer flex items-center gap-2.5 group"
                        >
                          <FaVideo className="text-sm" />
                          <span>Join Interactive Live Studio</span>
                          <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                      </Link>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            if (previewSecondsLeft > 0) {
                              toast.info("You have an 8-minute Free Live Preview for this session. Upgrade anytime for unlimited interactive access!");
                            } else {
                              setIsPreviewModalOpen(true);
                            }
                          }}
                          className="rounded-full bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200] text-white px-8 py-3.5 text-xs sm:text-sm font-black shadow-[0_15px_40px_rgba(242,154,12,0.65)] hover:shadow-[0_20px_50px_rgba(242,154,12,0.85)] transition-all cursor-pointer flex items-center gap-2"
                        >
                          <FaPlay className="text-xs" />
                          <span>{previewSecondsLeft > 0 ? `Free Live Preview (${Math.floor(previewSecondsLeft / 60)}m left)` : "Upgrade to Join Studio"}</span>
                        </button>

                        <Link to="/pricing">
                          <motion.button
                            whileHover={{ scale: 1.03, y: -2 }}
                            whileTap={{ scale: 0.96 }}
                            className="rounded-full border-2 border-[#F29A0C]/70 bg-black/50 backdrop-blur-xl text-white px-7 py-3.5 text-xs sm:text-sm font-extrabold hover:border-[#FFD166] hover:bg-[#F29A0C]/20 transition-all cursor-pointer flex items-center gap-2 shadow-lg"
                          >
                            <FaCrown className="text-[#FFD166] text-sm" />
                            <span>Upgrade Plan</span>
                          </motion.button>
                        </Link>
                      </>
                    )}
                  </div>

                </div>

                {/* Right Col: Video Player Preview (5 Cols) */}
                <div className="lg:col-span-5 relative">
                  <div className="relative rounded-[28px] overflow-hidden border-2 border-[#F29A0C]/60 bg-black/90 shadow-[0_20px_50px_rgba(0,0,0,0.7)] group/player">
                    
                    {/* Live Stream Video Player Component */}
                    <LiveStreamPlayer
                      session={activeLiveSession}
                      isMuted={isPlayerMuted}
                      toggleMute={() => setIsPlayerMuted(!isPlayerMuted)}
                      userRank={userRank}
                      previewSecondsLeft={previewSecondsLeft}
                      onPreviewExpired={() => setIsPreviewModalOpen(true)}
                      isLoggedIn={auth.loggedIn}
                    />

                    {/* Preview Progress Bar at bottom of video (for logged-in Free tier) */}
                    {auth.loggedIn && userRank === 0 && previewSecondsLeft > 0 && (
                      <div className="w-full bg-white/10 h-1.5">
                        <div
                          className="bg-gradient-to-r from-[#F29A0C] to-[#FF8400] h-full transition-all duration-1000"
                          style={{ width: `${(previewSecondsLeft / 480) * 100}%` }}
                        />
                      </div>
                    )}

                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════════════
          3. UPCOMING SCHEDULE TIMETABLE (PURELY REAL DATABASE SESSIONS IN IST)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="relative z-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="p-6 sm:p-8 rounded-[36px] bg-white/95 border-2 border-[#F4D9B5] shadow-[0_20px_60px_rgba(242,154,12,0.1)] backdrop-blur-2xl space-y-6">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#F4D9B5]/60 pb-5">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#F29A0C]/10 text-[#F29A0C] text-xs font-bold uppercase tracking-wider mb-2">
                <FaClock />
                <span>All Timings in Indian Standard Time (IST)</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] leading-tight tracking-[0.02em]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Upcoming Scheduled Live Batches
              </h2>
              <p className="text-xs sm:text-sm text-[#666666] font-medium mt-1">
                Explore live masterclasses scheduled by our certified master faculty in Indian Standard Time.
              </p>
            </div>

            <div className="flex items-center gap-3 self-start md:self-auto">
              <button
                onClick={() => fetchSessions(true)}
                className="p-2.5 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#F29A0C] hover:bg-[#F29A0C] hover:text-white transition-all cursor-pointer text-xs shadow-sm"
                title="Refresh live schedule"
              >
                <FiRefreshCw className={loading ? "animate-spin" : ""} />
              </button>
              <div className="text-xs font-bold text-[#F29A0C] bg-[#FFF8EE] px-4 py-2 rounded-full border border-[#F4D9B5]">
                Showing {upcomingSessions.length} Scheduled Batches
              </div>
            </div>
          </div>

          {/* Tier & Category Filter Pills */}
          <div className="space-y-4">
            
            {/* Category Filter */}
            <div className="ud-scroll ud-scroll-pad flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-[#555555] mr-2">Discipline:</span>
              {CATEGORIES.map((cat) => {
                const active = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 cursor-pointer ${
                      active
                        ? "bg-[#F29A0C] text-white shadow-md scale-105"
                        : "bg-[#FFF8EE] text-[#444444] border border-[#F4D9B5] hover:border-[#F29A0C] hover:bg-white"
                    }`}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Tier Filter */}
            <div className="ud-scroll ud-scroll-pad flex flex-wrap items-center gap-2 pt-1 border-t border-[#F4D9B5]/40">
              <span className="text-xs font-bold text-[#555555] mr-2">Membership Tier:</span>
              {TIER_FILTERS.map((tier) => {
                const active = selectedTier === tier.id;
                return (
                  <button
                    key={tier.id}
                    onClick={() => setSelectedTier(tier.id)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-300 cursor-pointer ${
                      active
                        ? "bg-[#111111] text-[#FFD166] shadow-sm scale-105"
                        : "bg-[#FFFDF9] text-[#666666] border border-[#F4D9B5] hover:border-[#111111]"
                    }`}
                  >
                    {tier.label}
                  </button>
                );
              })}
            </div>

          </div>

          {/* ── SESSIONS GRID OR EMPTY STATE ── */}
          {loading ? (
            <div className="py-20 text-center space-y-3">
              <div className="w-12 h-12 border-4 border-[#F29A0C] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm font-bold text-[#666666]">Loading real-time scheduled live batches...</p>
            </div>
          ) : upcomingSessions.length === 0 ? (
            <div className="py-16 text-center space-y-4 rounded-3xl bg-[#FFF8EE]/60 border border-[#F4D9B5] p-8">
              <div className="w-16 h-16 rounded-3xl bg-[#F29A0C]/15 text-[#F29A0C] flex items-center justify-center text-3xl mx-auto shadow-sm">
                <FaCalendarAlt />
              </div>
              <h3 className="text-xl font-bold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                No Upcoming Live Sessions Scheduled
              </h3>
              <p className="text-xs sm:text-sm text-[#666666] max-w-md mx-auto leading-relaxed">
                Our master faculty regularly schedules morning, afternoon, and evening live batches in Indian Standard Time. Please check back shortly or explore our curriculum.
              </p>
              <div className="pt-2">
                <Link to="/courses">
                  <button className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-xs font-bold shadow-md hover:scale-105 transition-transform cursor-pointer">
                    Explore All Yoga Curriculums
                  </button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
              {upcomingSessions.map((session, idx) => {
                const sessionRank = getTierRank(session.tier);
                const isEligible = userRank >= sessionRank;
                const istRange = getISTRange(session.scheduledAt, session.durationInMinutes);
                const istDate = formatToIST(session.scheduledAt);
                const tierConfig = TIERS_CONFIG[normalizeTierName(session.tier)] || TIERS_CONFIG.Basic;
                const isCurrentlyLive = session.isLive;

                return (
                  <motion.div
                    key={session.id || idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.06 }}
                    whileHover={{ y: -6, scale: 1.015 }}
                    className={`rounded-[32px] bg-gradient-to-b from-white via-white to-[#FFF8EE]/90 border-2 ${isCurrentlyLive ? "border-[#EF4444] shadow-[0_15px_45px_rgba(239,68,68,0.2)]" : "border-[#F4D9B5] shadow-[0_15px_45px_rgba(242,154,12,0.08)]"} p-6 hover:shadow-[0_25px_60px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] transition-all duration-300 flex flex-col justify-between relative overflow-hidden group`}
                  >
                    {/* Top Color Accent Line */}
                    <div className={`absolute top-0 left-0 right-0 h-1.5 ${isCurrentlyLive ? "bg-[#EF4444] animate-pulse" : "bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200]"}`} />

                    <div className="space-y-4 relative z-10">
                      
                      {/* Top Badges */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full border shadow-sm"
                            style={{ color: tierConfig.color, backgroundColor: tierConfig.bg, borderColor: tierConfig.border }}
                          >
                            {tierConfig.badge}
                          </span>
                          {isCurrentlyLive && (
                            <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#EF4444] text-white flex items-center gap-1 animate-pulse">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                              Live Now
                            </span>
                          )}
                        </div>

                        <span className="text-[11px] font-bold text-[#6B8A5A] bg-[#6B8A5A]/10 px-2.5 py-0.5 rounded-full border border-[#6B8A5A]/20">
                          {session.category || "Live Batch"}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-[#111111] group-hover:text-[#F29A0C] transition-colors leading-snug" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {session.title}
                      </h3>

                      {/* IST Time Badge */}
                      <div className="p-3 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5] flex items-center gap-3 text-xs text-[#222222]">
                        <div className="w-8 h-8 rounded-xl bg-[#F29A0C]/15 text-[#F29A0C] flex items-center justify-center text-sm font-bold flex-shrink-0">
                          <FaClock />
                        </div>
                        <div>
                          <div className="font-extrabold text-[#111111]">{istRange}</div>
                          <div className="text-[11px] text-[#666666] font-medium">{istDate.date} ({istDate.day}) • Indian Standard Time</div>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-xs text-[#555555] leading-relaxed line-clamp-2">
                        {session.description || "Interactive posture corrections and structured Vedic yoga practice."}
                      </p>

                      {/* Faculty Info */}
                      <div className="flex items-center justify-between pt-2 border-t border-[#F4D9B5]/60 text-xs">
                        <div className="flex items-center gap-2">
                          <GiLotus className="text-[#F29A0C] text-sm" />
                          <span className="font-bold text-[#111111]">{session.instructorName || "Master Faculty"}</span>
                        </div>
                        <div className="text-[11px] text-[#666666] font-medium">
                          {session.durationInMinutes || 60} Mins
                        </div>
                      </div>

                    </div>

                    {/* Card Action Button */}
                    <div className="pt-5 mt-5 border-t border-[#F4D9B5]/60 relative z-10">
                      {isCurrentlyLive ? (
                        <Link to={`/live-room/${session.id}`} className="block w-full">
                          <button className="w-full py-3 rounded-full bg-gradient-to-r from-[#EF4444] to-[#DC2626] text-white text-xs font-black shadow-[0_10px_25px_rgba(239,68,68,0.4)] hover:shadow-[0_15px_35px_rgba(239,68,68,0.6)] transition-all cursor-pointer flex items-center justify-center gap-2">
                            <FaVideo />
                            <span>Join Live Session Now</span>
                          </button>
                        </Link>
                      ) : isEligible ? (
                        <Link to={auth.loggedIn ? "/user/livesession" : "/register"} className="block w-full">
                          <button className="w-full py-3 rounded-full bg-[#FFF8EE] border-2 border-[#F4D9B5] text-[#111111] text-xs font-black hover:border-[#F29A0C] hover:bg-[#F29A0C] hover:text-white transition-all cursor-pointer flex items-center justify-center gap-2">
                            <FaCalendarAlt />
                            <span>Book Scheduled Slot</span>
                          </button>
                        </Link>
                      ) : (
                        <Link to={auth.loggedIn ? "/pricing" : "/register"} className="block w-full">
                          <button className="w-full py-3 rounded-full bg-black text-[#FFD166] border border-[#F29A0C]/50 text-xs font-black hover:bg-[#F29A0C] hover:text-white transition-all cursor-pointer flex items-center justify-center gap-2">
                            <FaLock />
                            <span>Unlock with {tierConfig.label}</span>
                          </button>
                        </Link>
                      )}
                    </div>

                  </motion.div>
                );
              })}
            </div>
          )}

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          4. PILLARS OF EXCELLENCE & TRUST STRIP
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 lg:px-12 max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -6, scale: 1.02 }}
            className="p-8 rounded-[34px] bg-gradient-to-br from-white/95 via-white/90 to-[#FFF8EE]/90 border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(242,154,12,0.08)] hover:shadow-[0_25px_60px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] flex flex-col items-center text-center space-y-3.5 backdrop-blur-2xl transition-all duration-300 relative overflow-hidden group"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6B8A5A]/20 to-[#6B8A5A]/5 border border-[#6B8A5A]/30 flex items-center justify-center text-[#6B8A5A] text-3xl shadow-sm group-hover:scale-110 transition-transform">
              <FaCertificate />
            </div>
            <h4 className="text-lg font-extrabold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Yoga Alliance USA Certified
            </h4>
            <p className="text-xs text-[#666666] leading-relaxed">
              Every live curriculum follows strict global alignment geometry, safe spinal articulation, and accredited international standards.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            whileHover={{ y: -6, scale: 1.02 }}
            className="p-8 rounded-[34px] bg-gradient-to-br from-white/95 via-white/90 to-[#FFF8EE]/90 border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(242,154,12,0.08)] hover:shadow-[0_25px_60px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] flex flex-col items-center text-center space-y-3.5 backdrop-blur-2xl transition-all duration-300 relative overflow-hidden group"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F29A0C]/20 to-[#F29A0C]/5 border border-[#F29A0C]/30 flex items-center justify-center text-[#F29A0C] text-3xl shadow-sm group-hover:scale-110 transition-transform">
              <FaAward />
            </div>
            <h4 className="text-lg font-extrabold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Live Posture Corrections
            </h4>
            <p className="text-xs text-[#666666] leading-relaxed">
              Experience real-time verbal posture adjustments and breathing rhythm synchronization directly from certified master teachers.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -6, scale: 1.02 }}
            className="p-8 rounded-[34px] bg-gradient-to-br from-white/95 via-white/90 to-[#FFF8EE]/90 border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(242,154,12,0.08)] hover:shadow-[0_25px_60px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] flex flex-col items-center text-center space-y-3.5 backdrop-blur-2xl transition-all duration-300 relative overflow-hidden group"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6B8A5A]/20 to-[#6B8A5A]/5 border border-[#6B8A5A]/30 flex items-center justify-center text-[#6B8A5A] text-3xl shadow-sm group-hover:scale-110 transition-transform">
              <FaGlobeAsia />
            </div>
            <h4 className="text-lg font-extrabold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Global Synchronized Sadhana
            </h4>
            <p className="text-xs text-[#666666] leading-relaxed">
              Connect and chant with dedicated practitioners across 15+ countries simultaneously in a high-vibrational virtual ashram.
            </p>
          </motion.div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          5. FREQUENTLY ASKED QUESTIONS ACCORDION
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 max-w-4xl mx-auto space-y-8">
        <div className="text-center space-y-2.5">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/20 text-[#F29A0C] text-xs font-bold uppercase tracking-wider">
            <FaQuestionCircle />
            <span>Live Class Clarifications</span>
          </div>
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Frequently Asked Questions
          </h3>
          <p className="text-xs sm:text-sm text-[#666666] max-w-xl mx-auto">
            Everything you need to know regarding camera requirements, Indian Standard Time conversion, and replays.
          </p>
        </div>

        <div className="space-y-4">
          {FAQS.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="rounded-[28px] border-2 border-[#F4D9B5] bg-gradient-to-r from-white/95 via-white/90 to-[#FFF8EE]/90 shadow-[0_12px_35px_rgba(242,154,12,0.05)] overflow-hidden transition-all duration-300 hover:border-[#F29A0C] backdrop-blur-xl"
              >
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full px-6 sm:px-8 py-5 flex items-center justify-between text-left font-bold text-[#111111] text-sm sm:text-base focus:outline-none transition-colors duration-300 hover:text-[#F29A0C] cursor-pointer"
                >
                  <span className="flex items-center gap-3.5 pr-4">
                    <GiLotus className="text-[#F29A0C] flex-shrink-0 text-xl" />
                    <span className="font-extrabold">{faq.q}</span>
                  </span>
                  <FaChevronDown
                    size={13}
                    className={`text-[#888888] flex-shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#F29A0C]" : ""}`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 sm:px-8 pb-6 pt-1 text-[#444444] text-xs sm:text-sm leading-relaxed pl-12 sm:pl-14 border-t border-[#F4D9B5]/50 bg-[#FFFDFB]">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          6. GRAND LUXURY ASHRAM SANCTUARY CTA BANNER (BROWN & GLOWING ORANGE)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 sm:px-6 lg:px-12 relative overflow-hidden bg-gradient-to-b from-[#FFF9F2] via-[#FFF3DF] to-[#FFF9F2]">
        <div className="max-w-[1440px] mx-auto relative z-10">
          <div className="relative">
            
            {/* Vivid Orange Glowing Aura Behind the Tilt Card Border */}
            <div className="absolute -inset-2.5 bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E97818] rounded-[48px] blur-2xl opacity-60 pointer-events-none animate-pulse" />
            <div className="absolute -inset-4 bg-gradient-to-r from-[#F29A0C]/40 via-[#FFA41B]/30 to-[#F29A0C]/40 rounded-[56px] blur-3xl opacity-70 pointer-events-none" />

            <LuxuryTiltCard maxTilt={4}>
              <div
                className="relative overflow-hidden rounded-[36px] sm:rounded-[44px] border-2 border-[#F29A0C] text-white p-6 sm:p-10 lg:p-14 shadow-[0_0_50px_rgba(242,154,12,0.45),0_30px_90px_rgba(0,0,0,0.6)] transition-shadow duration-500 group backdrop-blur-3xl"
                style={{
                  background: "linear-gradient(135deg, rgba(26, 12, 5, 0.96) 0%, rgba(44, 20, 8, 0.93) 50%, rgba(18, 8, 3, 0.98) 100%)",
                }}
              >
                {/* Background Sacred Mandala Blend */}
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-65 mix-blend-screen scale-[1.02] pointer-events-none"
                  style={{ backgroundImage: "url('/assets/trainers_bottom_bg.jpg')" }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#1A0C05]/95 via-[#2C1408]/75 to-[#1A0C05]/90 pointer-events-none" />

                {/* Rotating Sacred Om in Corner */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                  className="absolute -right-20 -bottom-20 w-[380px] h-[380px] opacity-15 text-[#FFD166] pointer-events-none"
                >
                  <FaOm className="w-full h-full" />
                </motion.div>

                {/* Top Golden Shimmer Line */}
                <div
                  className="absolute top-0 left-0 right-0 h-[4px]"
                  style={{
                    background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #FF8400, #F29A0C, transparent)",
                  }}
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center relative z-10">
                  
                  {/* Left Column (7 Cols) */}
                  <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
                    <div className="inline-flex items-center gap-2.5 rounded-full border border-[#F29A0C]/60 bg-black/60 px-4 py-2 backdrop-blur-xl shadow-[0_0_15px_rgba(242,154,12,0.3)]">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#F29A0C] animate-ping" />
                      <span className="text-[#FFD166] text-[11px] tracking-[2.5px] uppercase font-black">
                        SACRED LIVING YOGA STUDIO · LIVE SESSIONS
                      </span>
                    </div>

                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.15] tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      Transform Your Practice with <br />
                      <span className="bg-gradient-to-r from-[#FFD166] via-[#FFA41B] to-[#F29A0C] bg-clip-text text-transparent drop-shadow-[0_4px_30px_rgba(242,154,12,0.7)]">
                        Daily Live Guidance
                      </span>
                    </h2>

                    <p className="text-xs sm:text-sm md:text-base text-white/90 leading-relaxed max-w-xl mx-auto lg:mx-0 font-medium drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                      Join hundreds of active yogis practicing live across morning and evening Indian Standard Time batches. Step into a world of authentic yogic transmission.
                    </p>

                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                      <Link to={auth.loggedIn ? "/user/livesession" : "/register"}>
                        <motion.button
                          whileHover={{ scale: 1.04, y: -2 }}
                          whileTap={{ scale: 0.96 }}
                          className="rounded-full bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200] text-white px-8 sm:px-10 py-4 text-xs sm:text-sm font-black shadow-[0_15px_40px_rgba(242,154,12,0.65)] hover:shadow-[0_20px_50px_rgba(242,154,12,0.85)] transition-all cursor-pointer flex items-center gap-2.5 group"
                        >
                          <span>{auth.loggedIn ? "Go to Live Classroom" : "Begin Live Journey"}</span>
                          <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                      </Link>

                      <Link to="/pricing">
                        <motion.button
                          whileHover={{ scale: 1.03, y: -2 }}
                          whileTap={{ scale: 0.96 }}
                          className="rounded-full border-2 border-[#F29A0C]/70 bg-black/50 backdrop-blur-xl text-white px-7 sm:px-8 py-4 text-xs sm:text-sm font-extrabold hover:border-[#FFD166] hover:bg-[#F29A0C]/20 transition-all cursor-pointer flex items-center gap-2 shadow-lg"
                        >
                          <FaCrown className="text-[#FFD166] text-base" />
                          <span>Explore Tier Memberships</span>
                        </motion.button>
                      </Link>
                    </div>
                  </div>

                  {/* Right Column (5 Cols) - Clean Yogic Badges (Zero Instructor Portrait Images) */}
                  <div className="lg:col-span-5 flex flex-col gap-4 w-full">
                    
                    {/* Highlight Card 1 */}
                    <motion.div
                      whileHover={{ scale: 1.02, y: -3 }}
                      className="rounded-[24px] border-2 border-[#F29A0C]/50 bg-[#241006]/95 backdrop-blur-2xl p-4.5 sm:p-5 shadow-[0_15px_40px_rgba(0,0,0,0.5)] hover:border-[#F29A0C] transition-all"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="w-14 h-14 min-w-[56px] min-h-[56px] rounded-2xl bg-[#F29A0C]/20 border border-[#F29A0C] text-[#FFD166] flex items-center justify-center text-2xl flex-shrink-0 shadow-sm">
                          <GiLotus />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-sm font-black text-white truncate">Master Yogic Pedagogy</h4>
                          <p className="text-[11px] text-[#FFD166] font-bold tracking-wide">Classical Vedic Lineage</p>
                          <p className="text-[11px] text-white/80 mt-1 leading-snug font-medium italic line-clamp-2">
                            "Live alignment preserves energy flow and awakens physical stability."
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Highlight Card 2 */}
                    <motion.div
                      whileHover={{ scale: 1.02, y: -3 }}
                      className="rounded-[24px] border-2 border-white/40 bg-gradient-to-br from-[#F29A0C] via-[#FF8400] to-[#E97818] p-4.5 sm:p-5 shadow-[0_20px_50px_rgba(242,154,12,0.55)] text-white"
                    >
                      <div className="flex items-start gap-3.5">
                        <div className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-xl bg-white/20 backdrop-blur-xl flex items-center justify-center text-xl shadow-inner flex-shrink-0">
                          <GiMeditation className="text-white text-2xl" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-black">100% Live 2-Way Cameras</h4>
                            <span className="px-2 py-0.5 rounded-full bg-white/25 text-[10px] font-extrabold uppercase tracking-wide">Live Studio</span>
                          </div>
                          <p className="text-[11px] text-white/95 mt-1 leading-snug font-medium">
                            Real-time camera posture corrections & personalized alignment feedback in every session.
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Highlight Card 3 */}
                    <motion.div
                      whileHover={{ scale: 1.02, y: -3 }}
                      className="rounded-[24px] border-2 border-[#F29A0C]/50 bg-[#241006]/95 backdrop-blur-2xl p-4.5 sm:p-5 shadow-[0_15px_40px_rgba(0,0,0,0.5)] hover:border-[#F29A0C] transition-all"
                    >
                      <div className="flex items-start gap-3.5">
                        <div className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-xl bg-[#6B8A5A]/30 border border-[#6B8A5A]/50 text-[#9DC08B] flex items-center justify-center text-xl font-black shadow-sm flex-shrink-0">
                          <FaCertificate />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-black text-white">Yoga Alliance USA</h4>
                            <span className="px-2 py-0.5 rounded-full bg-[#6B8A5A]/30 text-[#9DC08B] text-[10px] font-bold border border-[#6B8A5A]/40">RYT-200</span>
                          </div>
                          <p className="text-[11px] text-white/80 mt-1 leading-snug font-medium">
                            International certification recognized by yoga institutions worldwide.
                          </p>
                        </div>
                      </div>
                    </motion.div>

                  </div>

                </div>
              </div>
            </LuxuryTiltCard>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          7. ROYAL GUEST PREVIEW UPGRADE MODAL
      ══════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {isPreviewModalOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsPreviewModalOpen(false)}
              className="fixed inset-0 bg-black/75 backdrop-blur-md"
            />

            {/* Modal Dialog Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-2xl bg-gradient-to-br from-[#1F0E04] via-[#2A1306] to-[#160A03] rounded-[36px] border-2 border-[#F29A0C] shadow-[0_30px_100px_rgba(242,154,12,0.4)] z-10 p-6 sm:p-10 text-white overflow-hidden"
            >
              {/* Top Golden Shimmer Line */}
              <div
                className="absolute top-0 left-0 right-0 h-[4px]"
                style={{
                  background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #FF8400, #F29A0C, transparent)",
                }}
              />

              {/* Close Button */}
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-black/60 border border-[#F29A0C]/50 text-white hover:text-[#F29A0C] flex items-center justify-center text-lg transition-all cursor-pointer z-20"
                aria-label="Close modal"
              >
                <FiX />
              </button>

              <div className="text-center space-y-4 pt-2">
                <div className="w-16 h-16 rounded-3xl bg-[#F29A0C]/20 border border-[#F29A0C] text-[#FFD166] flex items-center justify-center text-3xl mx-auto shadow-[0_0_25px_rgba(242,154,12,0.4)]">
                  <FaCrown />
                </div>

                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#EF4444]/20 border border-[#EF4444]/40 text-[#FFA41B] text-xs font-bold uppercase tracking-wider">
                  <FaRegClock />
                  <span>Free Live Preview Expired</span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Continue Your Live Journey with Our <br />
                  <span className="bg-gradient-to-r from-[#FFD166] via-[#FFA41B] to-[#F29A0C] bg-clip-text text-transparent">
                    Living Masters
                  </span>
                </h3>

                <p className="text-xs sm:text-sm text-white/85 max-w-lg mx-auto leading-relaxed">
                  You have experienced our free session preview. Upgrade your membership to unlock full two-way interactive classes, real-time camera posture corrections, and 24/7 video replays.
                </p>

                {/* Tier Highlights Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-left">
                  {[
                    { name: "Basic Plan", tag: "Daily Sadhana", price: "Starting ₹799/mo", desc: "Access to daily morning & evening live batches." },
                    { name: "Standard Plan", tag: "Most Popular", price: "Starting ₹1,499/mo", desc: "All Asana + Pranayama batches & live recordings." },
                    { name: "Premium Plan", tag: "Deep Healing", price: "Starting ₹2,499/mo", desc: "Unlimited live classes & 1-on-1 alignment desk." },
                  ].map((p, i) => (
                    <div
                      key={i}
                      className="p-4 rounded-2xl bg-black/60 border border-[#F29A0C]/40 space-y-1.5 hover:border-[#FFD166] transition-colors"
                    >
                      <span className="text-[10px] font-black uppercase text-[#F29A0C]">{p.tag}</span>
                      <h4 className="text-sm font-bold text-white">{p.name}</h4>
                      <p className="text-[11px] text-white/70 leading-snug">{p.desc}</p>
                      <div className="text-xs font-bold text-[#FFD166] pt-1">{p.price}</div>
                    </div>
                  ))}
                </div>

                {/* Modal CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4 border-t border-white/15">
                  <Link
                    to={auth.loggedIn ? "/pricing" : "/register"}
                    onClick={() => setIsPreviewModalOpen(false)}
                    className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#E08200] text-white text-xs font-black shadow-[0_15px_40px_rgba(242,154,12,0.6)] hover:scale-105 transition-transform flex items-center justify-center gap-2"
                  >
                    <span>{auth.loggedIn ? "Upgrade Membership" : "Register to Continue Live"}</span>
                    <FiArrowRight />
                  </Link>

                  <button
                    onClick={() => setIsPreviewModalOpen(false)}
                    className="w-full sm:w-auto px-6 py-3.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-bold hover:bg-white/20 transition-colors cursor-pointer"
                  >
                    Browse Schedule
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}

